import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { QuantityStepper } from '@/components/ui/quantity-stepper'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TicketType } from '@/components/events/public-ticket-picker'
import { ApiError, postJSON } from '@/lib/api'

type WaitlistEntry = {
  id: number
  name: string
  email: string
  party_size: number
  event_ticket_type_id: number
}
type WaitlistResponse = { waitlist_entry: WaitlistEntry }

export function PublicEventWaitlist({
  eventId,
  ticketTypes,
  initialTicket,
}: {
  eventId: number
  ticketTypes: TicketType[]
  initialTicket?: TicketType
}) {
  const [joining, setJoining] = React.useState(Boolean(initialTicket))
  const [ticketId, setTicketId] = React.useState(
    initialTicket?.id?.toString() ?? ticketTypes[0]?.id?.toString() ?? '',
  )
  const [entry, setEntry] = React.useState<WaitlistEntry | null>(null)
  const ticket = ticketTypes.find((candidate) => candidate.id === Number(ticketId))
  const join = useMutation({
    mutationFn: (values: { name: string; email: string; partySize: number }) =>
      postJSON<WaitlistResponse>(`/api/v1/public/events/${eventId}/waitlist_entries`, {
        waitlist_entry: {
          name: values.name,
          email: values.email,
          party_size: values.partySize,
          event_ticket_type_id: Number(ticketId),
        },
      }),
    onSuccess: ({ waitlist_entry }) => setEntry(waitlist_entry),
  })

  if (entry) {
    const entryTicket = ticketTypes.find((candidate) => candidate.id === entry.event_ticket_type_id)
    return <WaitlistStatus entry={entry} ticketName={entryTicket?.name ?? 'Selected ticket'} />
  }

  if (!joining) {
    return (
      <div className="border-t border-border px-4 py-3">
        <Button type="button" className="h-8 w-full px-3" onClick={() => setJoining(true)}>
          Join waitlist
        </Button>
        <p className="mt-2 text-caption text-muted-foreground">
          We will email you if a seat opens up.
        </p>
      </div>
    )
  }

  return (
    <WaitlistForm
      ticket={ticket}
      ticketTypes={ticketTypes}
      ticketId={ticketId}
      setTicketId={setTicketId}
      join={join}
    />
  )
}

function WaitlistForm({
  ticket,
  ticketTypes,
  ticketId,
  setTicketId,
  join,
}: {
  ticket: TicketType | undefined
  ticketTypes: TicketType[]
  ticketId: string
  setTicketId: (value: string) => void
  join: ReturnType<
    typeof useMutation<WaitlistResponse, Error, { name: string; email: string; partySize: number }>
  >
}) {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [partySize, setPartySize] = React.useState(1)
  const message =
    join.error instanceof ApiError && isErrorBody(join.error.body)
      ? Object.values(join.error.body.errors).flat().join(' ')
      : join.isError
        ? 'Unable to join the waitlist. Please try again.'
        : null

  return (
    <form
      className="space-y-3 border-t border-border px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault()
        join.mutate({ name, email, partySize })
      }}
    >
      <p className="text-body-sm font-medium text-foreground">Join the waitlist</p>
      {message && <Alert>{message}</Alert>}
      <label htmlFor="waitlist-name" className="block space-y-1 text-caption text-muted-foreground">
        <span>Name</span>
        <Input
          id="waitlist-name"
          className="h-8 bg-background"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label
        htmlFor="waitlist-email"
        className="block space-y-1 text-caption text-muted-foreground"
      >
        <span>Email</span>
        <Input
          id="waitlist-email"
          type="email"
          className="h-8 bg-background"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <div className="space-y-1">
        <p className="text-caption text-muted-foreground">Ticket type</p>
        <Select value={ticketId} onValueChange={setTicketId}>
          <SelectTrigger className="h-8" aria-label="Ticket type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ticketTypes.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id.toString()}>
                {candidate.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-caption text-muted-foreground">Party size</p>
        <QuantityStepper
          label="Party size"
          value={partySize}
          min={1}
          max={ticket?.max_per_order ?? 20}
          onChange={setPartySize}
        />
      </div>
      <Button type="submit" className="h-8 w-full px-3" disabled={join.isPending || !ticket}>
        Join waitlist
      </Button>
      <p className="text-caption text-muted-foreground">
        No payment now. If a seat opens, we will email you a link to check out.
      </p>
    </form>
  )
}

function WaitlistStatus({ entry, ticketName }: { entry: WaitlistEntry; ticketName: string }) {
  return (
    <div className="border-t border-border bg-primary/5 px-4 py-3" role="status">
      <p className="text-body-sm font-medium text-foreground">You are on the waitlist</p>
      <p className="mt-1 text-caption text-muted-foreground">
        {ticketName} · {entry.party_size} {entry.party_size === 1 ? 'seat' : 'seats'} ·{' '}
        {entry.email}
      </p>
      <p className="mt-1 text-caption text-muted-foreground">
        We will email you if a seat opens up. Nothing has been charged.
      </p>
    </div>
  )
}

function isErrorBody(value: unknown): value is { errors: Record<string, string[]> } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'errors' in value &&
    typeof (value as { errors: unknown }).errors === 'object'
  )
}
